import {
  InventoryListing,
  InventoryListingPhoneStatus,
  useUpdateInventoryManualFields,
  useHoldInventory,
  useSetRecommendedPrice,
  useAcknowledgeRecommendedPrice,
  useGetMe,
  getListInventoryQueryKey,
  getGetDashboardSummaryQueryKey,
  getListEventsQueryKey,
} from "@workspace/api-client-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { BankNumbersPopover } from "@/components/bank-numbers";
import { PhoneStatusIcon } from "@/components/phone-status-icon";
import {
  ChevronDown,
  ChevronRight,
  Check,
  Copy,
  ClipboardList,
  Lock,
  Unlock,
  QrCode,
  Link2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  NoteCell,
  RowActionsCell,
  FveStatusPill,
  PriceCell,
  ListingCell,
  TicketsCell,
  QrCoverageCell,
  LinkCoverageCell,
  seatSummary,
  SeatPill,
  displayQty,
  nat,
  naturalSeatCompare,
} from "@/components/inventory-cells";
import { InventoryMobileCard } from "@/components/inventory-card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFveTransitions } from "@/lib/use-fve-transitions";
import { showHoldOutcomeToast } from "@/lib/hold-toast";
import {
  useResizableColumns,
  ColumnResizeHandle,
  ResetColumnWidthsButton,
} from "@/hooks/use-resizable-columns";

const ACCOUNT_HEIGHT = 48;
const ROW_HEIGHT = 37;
const HEADER_HEIGHT = 36;

const NO_EMAIL = "(no email)";
// Sorts last when an account has no resolvable event date.
const NO_DATE_KEY = "9999-99-99";

const COLUMN_ORDER = [
  "select",
  "actions",
  "fve",
  "event",
  "seats",
  "qty",
  "price",
  "listing",
  "qr",
  "link",
  "sales",
  "note",
] as const;

const COL_WIDTHS: Record<string, string> = {
  select: "36px",
  actions: "104px",
  fve: "minmax(150px, 0.9fr)",
  event: "minmax(200px, 2fr)",
  seats: "130px",
  qty: "52px",
  price: "120px",
  listing: "100px",
  qr: "40px",
  link: "40px",
  sales: "120px",
  note: "minmax(120px, 1fr)",
};

const COL_LABELS: Record<string, string> = {
  actions: "Actions",
  fve: "FVE",
  event: "Event",
  seats: "Seats",
  qty: "Qty",
  price: "Price",
  listing: "Listing",
  qr: "QR",
  link: "Links",
  sales: "Sales",
  note: "Note",
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatEventDate(s?: string | null): string | null {
  if (!s) return null;
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
}

// Parse a listing's eventDate to a local midnight Date. Mirrors the
// midnight-normalised threshold the sidebar/home use to classify past vs.
// upcoming. Returns null when no date can be parsed (treated as upcoming).
function parseEventDate(s?: string | null): Date | null {
  if (!s) return null;
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

type PhoneStatus = "good" | "bad" | "unknown";

function normPhone(s?: InventoryListingPhoneStatus): PhoneStatus {
  return s === "good" || s === "bad" ? s : "unknown";
}

interface AccountGroup {
  key: string;
  email: string;
  phoneStatus: PhoneStatus;
  phoneRow: InventoryListing;
  bankRow: InventoryListing | null;
  dateKey: string;
  hasBadPhone: boolean;
  ids: number[];
  rows: InventoryListing[];
  upcomingRows: InventoryListing[];
  pastRows: InventoryListing[];
  pastEventCount: number;
  totalQty: number;
  heldCount: number;
  fveCount: number;
}

interface AccountInventoryTableProps {
  data: InventoryListing[];
  selectedIds: Set<number>;
  onToggleSelectAll: () => void;
  onSelectIds: (ids: number[], selected: boolean) => void;
  allSelected: boolean;
  defaultHoldDays: number;
  onBulkAccount: (ids: number[], action: "hold" | "unhold") => void;
  bulkPending: boolean;
}

type FlatItem =
  | { kind: "account"; key: string; group: AccountGroup }
  | { kind: "row"; key: string; row: InventoryListing };

export function AccountInventoryTable({
  data,
  selectedIds,
  onToggleSelectAll,
  onSelectIds,
  allSelected,
  defaultHoldDays,
  onBulkAccount,
  bulkPending,
}: AccountInventoryTableProps) {
  const queryClient = useQueryClient();

  // Holds and FVE toggles span events here, so invalidate the cross-event
  // inventory list (and the derived summaries) rather than a single event.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListInventoryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetDashboardSummaryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() });
  };

  const manualFields = useUpdateInventoryManualFields({ mutation: { onSuccess: invalidate } });
  const hold = useHoldInventory({
    mutation: {
      onSuccess: (outcome, variables) => {
        invalidate();
        showHoldOutcomeToast(outcome, variables.data.action);
      },
    },
  });
  const fve = useFveTransitions(invalidate);

  const { data: me } = useGetMe();
  const isAdmin = me?.role === "admin";
  const myPullerTag = me?.pullerTag ?? null;

  const setRecommended = useSetRecommendedPrice({
    mutation: {
      onSuccess: (res) => {
        invalidate();
        if (res.slackAlert.ok) {
          toast.success("Recommended price saved — puller notified via Slack");
        } else {
          toast.warning(
            `Recommended price saved, but the Slack alert failed${res.slackAlert.error ? `: ${res.slackAlert.error}` : ""}`,
          );
        }
      },
      onError: () => toast.error("Failed to save recommended price"),
    },
  });

  const acknowledgeRecommended = useAcknowledgeRecommendedPrice({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast.success("Acknowledged");
      },
      onError: () => toast.error("Failed to acknowledge"),
    },
  });

  const placeHold = (id: number, holdDays: number) =>
    hold.mutate({ id, data: { action: "hold", holdDays } });
  const releaseHold = (id: number) => hold.mutate({ id, data: { action: "unhold" } });

  const busyId = manualFields.isPending
    ? manualFields.variables?.id
    : hold.isPending
      ? hold.variables?.id
      : (fve.busyId ?? null);

  const isMobile = useIsMobile();
  const resizable = useResizableColumns("tex-acct-col-widths", COL_WIDTHS);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleAccount = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Per-account reveal of past listings. Hidden by default; resets on reload.
  const [pastExpanded, setPastExpanded] = useState<Set<string>>(new Set());
  const togglePast = (key: string) =>
    setPastExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Group listings by account (id when present, else email). Order accounts by
  // earliest event date, then problem phones first, then email alphabetically.
  const groups = useMemo<AccountGroup[]>(() => {
    const map = new Map<string, InventoryListing[]>();
    for (const r of data) {
      const key =
        r.accountId != null
          ? `id:${r.accountId}`
          : r.vendorAccountEmail
            ? `em:${r.vendorAccountEmail.toLowerCase()}`
            : "unknown";
      const arr = map.get(key);
      if (arr) arr.push(r);
      else map.set(key, [r]);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();

    const result: AccountGroup[] = [];
    for (const [key, rows] of map) {
      const sorted = [...rows].sort(
        (a, b) => nat(a.eventDate, b.eventDate) || naturalSeatCompare(a, b),
      );
      let dateKey = NO_DATE_KEY;
      for (const r of sorted) {
        if (r.eventDate && r.eventDate < dateKey) dateKey = r.eventDate;
      }
      // Partition into upcoming vs. past listings. A row is past only when its
      // event date is strictly before today (no-date rows count as upcoming),
      // matching the sidebar's past/upcoming split.
      const upcomingRows: InventoryListing[] = [];
      const pastRows: InventoryListing[] = [];
      for (const r of sorted) {
        const d = parseEventDate(r.eventDate);
        if (d && d.getTime() < todayMs) pastRows.push(r);
        else upcomingRows.push(r);
      }
      const pastEventCount = new Set(
        pastRows.map((r) => r.eventId ?? r.eventName ?? r.eventDate),
      ).size;
      const hasBadPhone = sorted.some((r) => normPhone(r.phoneStatus) === "bad");
      const hasGoodPhone = sorted.some((r) => normPhone(r.phoneStatus) === "good");
      const phoneStatus: PhoneStatus = hasBadPhone
        ? "bad"
        : hasGoodPhone
          ? "good"
          : "unknown";
      const phoneRow =
        sorted.find((r) => normPhone(r.phoneStatus) === phoneStatus) ?? sorted[0];
      const bankRow = sorted.find((r) => r.bankMappingStatus) ?? null;
      let totalQty = 0;
      let heldCount = 0;
      let fveCount = 0;
      for (const r of sorted) {
        totalQty += displayQty(r);
        if (r.adminHoldActive) heldCount += 1;
        if (r.listedOnFve) fveCount += 1;
      }
      result.push({
        key,
        email: sorted[0].vendorAccountEmail || NO_EMAIL,
        phoneStatus,
        phoneRow,
        bankRow,
        dateKey,
        hasBadPhone,
        ids: sorted.map((r) => r.id),
        rows: sorted,
        upcomingRows,
        pastRows,
        pastEventCount,
        totalQty,
        heldCount,
        fveCount,
      });
    }

    result.sort(
      (a, b) =>
        a.dateKey.localeCompare(b.dateKey) ||
        Number(b.hasBadPhone) - Number(a.hasBadPhone) ||
        nat(a.email, b.email),
    );
    return result;
  }, [data]);

  // Every visible account email, in render order, for the "copy all" action.
  const allEmails = useMemo(
    () => groups.map((g) => g.email).filter((e) => e !== NO_EMAIL),
    [groups],
  );

  const { items, stickyIndexes } = useMemo(() => {
    const list: FlatItem[] = [];
    const sticky: number[] = [];
    for (const g of groups) {
      sticky.push(list.length);
      list.push({ kind: "account", key: `a:${g.key}`, group: g });
      if (collapsed.has(g.key)) continue;
      for (const r of g.upcomingRows) {
        list.push({ kind: "row", key: `row:${r.id}`, row: r });
      }
      if (pastExpanded.has(g.key)) {
        for (const r of g.pastRows) {
          list.push({ kind: "row", key: `row:${r.id}`, row: r });
        }
      }
    }
    return { items: list, stickyIndexes: sticky };
  }, [groups, collapsed, pastExpanded]);

  const parentRef = useRef<HTMLDivElement>(null);
  const activeStickyIndexRef = useRef<number | null>(null);
  const isSticky = useCallback(
    (index: number) => stickyIndexes.includes(index),
    [stickyIndexes],
  );
  const isActiveSticky = (index: number) =>
    activeStickyIndexRef.current !== null && activeStickyIndexRef.current === index;

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      items[index]?.kind === "account" ? ACCOUNT_HEIGHT : ROW_HEIGHT,
    getItemKey: (index) => items[index]?.key ?? index,
    overscan: 12,
    rangeExtractor: useCallback(
      (range: { startIndex: number; endIndex: number; overscan: number; count: number }) => {
        let active: number | null = null;
        for (const i of stickyIndexes) {
          if (i <= range.startIndex) active = i;
          else break;
        }
        activeStickyIndexRef.current = active;
        const base = defaultRangeExtractor(range);
        const next = new Set(active !== null ? [active, ...base] : base);
        return [...next].sort((a, b) => a - b);
      },
      [stickyIndexes],
    ),
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [items, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // An event delivers either QR (international) or transfer links (domestic).
  // The account view can span both event types, so show a coverage column only
  // when at least one row belongs to that delivery type; a single-type account
  // collapses to one column. Driven by the event's configured marketType, not by
  // whether assets are uploaded yet.
  const showQrColumn = useMemo(
    () => data.some((r) => r.marketType === "international"),
    [data],
  );
  const showLinkColumn = useMemo(
    () => data.some((r) => r.marketType === "domestic"),
    [data],
  );
  const columnOrder = useMemo(
    () =>
      COLUMN_ORDER.filter((id) => {
        if (id === "qr") return showQrColumn;
        if (id === "link") return showLinkColumn;
        return true;
      }),
    [showQrColumn, showLinkColumn],
  );

  const { template: gridTemplate, minWidth: gridMinWidth } = useMemo(
    () => resizable.getLayout([...columnOrder]),
    [resizable.getLayout, columnOrder],
  );

  // Header mirrors the body's horizontal scroll so columns stay aligned when
  // resized widths overflow the panel.
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const syncHeaderScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  const groupSelectionState = (ids: number[]): boolean | "indeterminate" => {
    if (ids.length === 0) return false;
    let sel = 0;
    for (const id of ids) if (selectedIds.has(id)) sel += 1;
    if (sel === 0) return false;
    if (sel === ids.length) return true;
    return "indeterminate";
  };

  if (isMobile) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border/40 shrink-0">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={allSelected && data.length > 0}
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all"
            />
            Select all
          </label>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground font-mono">
              {groups.length} {groups.length === 1 ? "account" : "accounts"}
            </span>
            <CopyAllEmailsButton emails={allEmails} />
          </div>
        </div>
        <div className="flex-1 overflow-auto custom-scrollbar p-2 space-y-2">
          {items.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              No inventory listings match the current filters.
            </div>
          ) : (
            items.map((item) => {
              if (item.kind === "account") {
                const g = item.group;
                const isCollapsed = collapsed.has(g.key);
                const selState = groupSelectionState(g.ids);
                return (
                  <div
                    key={item.key}
                    className="rounded-md bg-panel border border-border px-3 py-2 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleAccount(g.key)}
                        className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        aria-label={isCollapsed ? "Expand account" : "Collapse account"}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                      <Checkbox
                        checked={selState}
                        onCheckedChange={() => onSelectIds(g.ids, selState !== true)}
                        aria-label={`Select all listings for ${g.email}`}
                        className="shrink-0"
                      />
                      <PhoneStatusIcon
                        status={g.phoneStatus}
                        lastSmsTime={g.phoneRow.lastSmsTime}
                        phoneType={g.phoneRow.phoneType}
                        phone={g.phoneRow.phoneOnFile}
                      />
                      <span
                        className={cn(
                          "text-sm font-medium truncate min-w-0",
                          g.email === NO_EMAIL
                            ? "text-muted-foreground italic"
                            : "text-foreground",
                        )}
                        title={g.email}
                      >
                        {g.email}
                      </span>
                      {g.email !== NO_EMAIL && <CopyEmailButton email={g.email} />}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <AccountSummaryBadges
                        listings={g.ids.length}
                        qty={g.totalQty}
                        held={g.heldCount}
                        fve={g.fveCount}
                      />
                      {g.bankRow ? (
                        <BankNumbersPopover
                          details={{
                            label:
                              g.bankRow.bankCardName ||
                              g.bankRow.bankName ||
                              g.bankRow.bankSource ||
                              "Bank",
                            variant: g.bankRow.bankVariant,
                            routingNumber: g.bankRow.bankRouting,
                            accountNumber: g.bankRow.bankAccount,
                            routingLast4: g.bankRow.bankRoutingLast4,
                            accountLast4: g.bankRow.bankAccountLast4,
                            variantId: g.bankRow.bankVariantId,
                            mappingStatus: g.bankRow.bankMappingStatus,
                            accountEmail: g.bankRow.vendorAccountEmail,
                          }}
                        />
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase"
                        >
                          No Bank
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[11px] px-2.5 bg-card rounded-full"
                        disabled={bulkPending}
                        onClick={() => onBulkAccount(g.ids, "hold")}
                        title={`Place a ${defaultHoldDays}-day hold on all ${g.ids.length} listing(s)`}
                        aria-label={`Hold all listings for ${g.email}`}
                      >
                        <Lock className="h-3 w-3 mr-1 text-amber-400" /> Hold all
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[11px] px-2.5 bg-card rounded-full text-emerald-500"
                        disabled={bulkPending || g.heldCount === 0}
                        onClick={() => onBulkAccount(g.ids, "unhold")}
                        title={
                          g.heldCount === 0
                            ? "No active holds to release"
                            : `Release holds on this account (${g.heldCount} held)`
                        }
                        aria-label={`Release all holds for ${g.email}`}
                      >
                        <Unlock className="h-3 w-3 mr-1" /> Release all
                      </Button>
                      {g.pastEventCount > 0 && (
                        <button
                          onClick={() => togglePast(g.key)}
                          className="text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1"
                        >
                          {pastExpanded.has(g.key)
                            ? "Hide past events"
                            : `Show ${g.pastEventCount} past event${g.pastEventCount === 1 ? "" : "s"}`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }

              const r = item.row;
              const eventDate = formatEventDate(r.eventDate);
              return (
                <div key={item.key} className="pl-3">
                  <InventoryMobileCard
                    row={r}
                    fve={fve}
                    defaultHoldDays={defaultHoldDays}
                    isBusy={busyId === r.id}
                    onHold={(days) => placeHold(r.id, days)}
                    onUnhold={() => releaseHold(r.id)}
                    isAdmin={isAdmin}
                    canAcknowledge={myPullerTag != null && myPullerTag === r.pullerTag}
                    onSavePrice={(price) =>
                      setRecommended.mutateAsync({
                        id: r.id,
                        data: { recommendedListPrice: price },
                      })
                    }
                    onAcknowledge={() => acknowledgeRecommended.mutateAsync({ id: r.id })}
                    selected={selectedIds.has(r.id)}
                    onToggleSelect={() => onSelectIds([r.id], !selectedIds.has(r.id))}
                    onSaveNote={(note) =>
                      manualFields.mutateAsync({
                        id: r.id,
                        data: { internalNotes: note ?? "" },
                      })
                    }
                    extraFields={[
                      {
                        label: "Event",
                        node: (
                          <span className="flex flex-wrap items-baseline gap-1.5">
                            <span className="text-xs">{r.eventName || "-"}</span>
                            {eventDate && (
                              <span className="font-mono text-[10px] text-muted-foreground">
                                {eventDate}
                              </span>
                            )}
                          </span>
                        ),
                        full: true,
                      },
                    ]}
                    showListing
                    showSales
                    showQr
                    showLink
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 border-b border-border/40 shrink-0">
        <span className="text-[11px] text-muted-foreground font-mono pl-1">
          {groups.length} {groups.length === 1 ? "account" : "accounts"}
        </span>
        <div className="flex items-center gap-1">
          <ResetColumnWidthsButton
            show={resizable.isCustomized}
            onReset={resizable.reset}
          />
          <CopyAllEmailsButton emails={allEmails} />
        </div>
      </div>

      {/* Column header (outside the scroll region so it never scrolls away).
          Horizontally it mirrors the body's scroll so columns stay aligned. */}
      <div ref={headerScrollRef} className="overflow-hidden shrink-0">
        <div
          className="grid bg-panel border-b border-border"
          style={{ gridTemplateColumns: gridTemplate, minWidth: gridMinWidth, height: HEADER_HEIGHT }}
        >
          {columnOrder.map((id) => (
            <div
              key={id}
              className="relative flex items-center px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium whitespace-nowrap select-none overflow-hidden"
            >
              {id === "select" ? (
                <Checkbox
                  checked={allSelected && data.length > 0}
                  onCheckedChange={onToggleSelectAll}
                  aria-label="Select all"
                />
              ) : id === "qr" ? (
                <span title="QR / PDF assets" aria-label="QR / PDF assets">
                  <QrCode className="h-3.5 w-3.5" />
                </span>
              ) : id === "link" ? (
                <span title="Transfer links" aria-label="Transfer links">
                  <Link2 className="h-3.5 w-3.5" />
                </span>
              ) : (
                COL_LABELS[id]
              )}
              <ColumnResizeHandle columnId={id} onResize={resizable.startResize} />
            </div>
          ))}
        </div>
      </div>

      <div
        ref={parentRef}
        className="flex-1 overflow-auto custom-scrollbar"
        onScroll={syncHeaderScroll}
      >
        {items.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">
            No inventory listings match the current filters.
          </div>
        ) : (
          <div style={{ height: totalSize, position: "relative", minWidth: gridMinWidth }}>
            {virtualRows.map((virtualRow) => {
              const item = items[virtualRow.index];
              if (!item) return null;
              const sticky = isSticky(virtualRow.index);
              const active = isActiveSticky(virtualRow.index);
              const style: React.CSSProperties = {
                position: active ? "sticky" : "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: virtualRow.size,
                zIndex: sticky ? 11 : 1,
                ...(active ? {} : { transform: `translateY(${virtualRow.start}px)` }),
              };

              if (item.kind === "account") {
                const g = item.group;
                const isCollapsed = collapsed.has(g.key);
                const selState = groupSelectionState(g.ids);
                return (
                  <div
                    key={virtualRow.key}
                    style={style}
                    className="flex items-center gap-2.5 px-3 bg-panel border-y border-border"
                  >
                    <button
                      onClick={() => toggleAccount(g.key)}
                      className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      aria-label={isCollapsed ? "Expand account" : "Collapse account"}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    <Checkbox
                      checked={selState}
                      onCheckedChange={() => onSelectIds(g.ids, selState !== true)}
                      aria-label={`Select all listings for ${g.email}`}
                      className="shrink-0"
                    />
                    <PhoneStatusIcon
                      status={g.phoneStatus}
                      lastSmsTime={g.phoneRow.lastSmsTime}
                      phoneType={g.phoneRow.phoneType}
                      phone={g.phoneRow.phoneOnFile}
                    />
                    <span
                      className={cn(
                        "text-sm font-medium truncate",
                        g.email === NO_EMAIL
                          ? "text-muted-foreground italic"
                          : "text-foreground",
                      )}
                      title={g.email}
                    >
                      {g.email}
                    </span>
                    {g.email !== NO_EMAIL && <CopyEmailButton email={g.email} />}
                    {g.pastEventCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePast(g.key);
                        }}
                        className="shrink-0 text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1"
                        aria-label={
                          pastExpanded.has(g.key)
                            ? `Hide past events for ${g.email}`
                            : `Show ${g.pastEventCount} past event${g.pastEventCount === 1 ? "" : "s"} for ${g.email}`
                        }
                      >
                        {pastExpanded.has(g.key)
                          ? "Hide past events"
                          : `Show ${g.pastEventCount} past event${g.pastEventCount === 1 ? "" : "s"}`}
                      </button>
                    )}
                    <div className="ml-auto flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[11px] px-2.5 bg-card rounded-full"
                          disabled={bulkPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            onBulkAccount(g.ids, "hold");
                          }}
                          title={`Place a ${defaultHoldDays}-day hold on all ${g.ids.length} listing(s)`}
                          aria-label={`Hold all listings for ${g.email}`}
                        >
                          <Lock className="h-3 w-3 mr-1 text-amber-400" /> Hold all
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[11px] px-2.5 bg-card rounded-full text-emerald-500"
                          disabled={bulkPending || g.heldCount === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onBulkAccount(g.ids, "unhold");
                          }}
                          title={
                            g.heldCount === 0
                              ? "No active holds to release"
                              : `Release holds on this account (${g.heldCount} held)`
                          }
                          aria-label={`Release all holds for ${g.email}`}
                        >
                          <Unlock className="h-3 w-3 mr-1" /> Release all
                        </Button>
                      </div>
                      <AccountSummaryBadges
                        listings={g.ids.length}
                        qty={g.totalQty}
                        held={g.heldCount}
                        fve={g.fveCount}
                      />
                      {g.bankRow ? (
                        <BankNumbersPopover
                          details={{
                            label:
                              g.bankRow.bankCardName ||
                              g.bankRow.bankName ||
                              g.bankRow.bankSource ||
                              "Bank",
                            variant: g.bankRow.bankVariant,
                            routingNumber: g.bankRow.bankRouting,
                            accountNumber: g.bankRow.bankAccount,
                            routingLast4: g.bankRow.bankRoutingLast4,
                            accountLast4: g.bankRow.bankAccountLast4,
                            variantId: g.bankRow.bankVariantId,
                            mappingStatus: g.bankRow.bankMappingStatus,
                            accountEmail: g.bankRow.vendorAccountEmail,
                          }}
                        />
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase"
                        >
                          No Bank
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              }

              const r = item.row;
              const selected = selectedIds.has(r.id);
              const fveListed = !!r.listedOnFve;
              const eventDate = formatEventDate(r.eventDate);
              return (
                <div
                  key={virtualRow.key}
                  style={{ ...style, gridTemplateColumns: gridTemplate, display: "grid" }}
                  className={cn(
                    "group border-b border-border/40 transition-colors items-center",
                    selected
                      ? "bg-primary/5"
                      : fveListed
                        ? "bg-emerald-500/[0.07] hover:bg-emerald-500/[0.12]"
                        : "hover:bg-muted/40",
                  )}
                >
                  <div className="px-3 overflow-hidden flex items-center" />
                  <div className="px-3 overflow-hidden flex items-center">
                    <RowActionsCell
                      row={r}
                      defaultHoldDays={defaultHoldDays}
                      isBusy={busyId === r.id}
                      onHold={(days) => placeHold(r.id, days)}
                      onUnhold={() => releaseHold(r.id)}
                      fve={fve}
                    />
                  </div>
                  <div className="px-3 overflow-hidden flex items-center">
                    <FveStatusPill row={r} fve={fve} />
                  </div>
                  <div className="px-3 overflow-hidden flex items-center gap-1.5 min-w-0">
                    <span className="text-xs truncate" title={r.eventName ?? undefined}>
                      {r.eventName || "-"}
                    </span>
                    {eventDate && (
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                        {eventDate}
                      </span>
                    )}
                  </div>
                  <div className="px-3 overflow-hidden flex items-center">
                    <SeatPill seat={seatSummary(r)} />
                  </div>
                  <div className="px-3 overflow-hidden flex items-center">
                    <span className="font-mono text-xs">{displayQty(r)}</span>
                  </div>
                  <div className="px-3 overflow-hidden flex items-center">
                    <PriceCell
                      row={r}
                      isAdmin={isAdmin}
                      canAcknowledge={
                        myPullerTag != null && myPullerTag === r.pullerTag
                      }
                      onSave={(price) =>
                        setRecommended.mutateAsync({
                          id: r.id,
                          data: { recommendedListPrice: price },
                        })
                      }
                      onAcknowledge={() =>
                        acknowledgeRecommended.mutateAsync({ id: r.id })
                      }
                    />
                  </div>
                  <div className="px-3 overflow-hidden flex items-center">
                    <ListingCell row={r} />
                  </div>
                  {showQrColumn && (
                    <div className="px-3 overflow-hidden flex items-center justify-center">
                      <QrCoverageCell row={r} />
                    </div>
                  )}
                  {showLinkColumn && (
                    <div className="px-3 overflow-hidden flex items-center justify-center">
                      <LinkCoverageCell row={r} />
                    </div>
                  )}
                  <div className="px-3 overflow-hidden flex items-center">
                    <TicketsCell listings={[r]} />
                  </div>
                  <div className="px-3 overflow-hidden flex items-center">
                    <NoteCell
                      row={r}
                      onSave={(note) =>
                        manualFields.mutateAsync({
                          id: r.id,
                          data: { internalNotes: note ?? "" },
                        })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountSummaryBadges({
  listings,
  qty,
  held,
  fve,
}: {
  listings: number;
  qty: number;
  held: number;
  fve: number;
}) {
  return (
    <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider">
      <span
        className="text-muted-foreground"
        title={`${listings} listing${listings === 1 ? "" : "s"}`}
      >
        {listings} lst
      </span>
      <span className="text-muted-foreground" title={`${qty} available`}>
        {qty} qty
      </span>
      {held > 0 && (
        <Badge
          variant="outline"
          className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase px-1.5 py-0"
          title={`${held} on hold`}
        >
          {held} held
        </Badge>
      )}
      {fve > 0 && (
        <Badge
          variant="outline"
          className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 font-mono text-[9px] uppercase px-1.5 py-0"
          title={`${fve} listed on FVE`}
        >
          {fve} fve
        </Badge>
      )}
    </div>
  );
}

function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(email);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      title="Copy account email"
      aria-label="Copy account email"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function CopyAllEmailsButton({ emails }: { emails: string[] }) {
  const [copied, setCopied] = useState(false);
  const disabled = emails.length === 0;
  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={disabled}
      className="h-6 text-[11px] px-2 text-muted-foreground hover:text-foreground"
      onClick={() => {
        if (disabled) return;
        navigator.clipboard?.writeText(emails.join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 mr-1 text-green-500" />
      ) : (
        <ClipboardList className="h-3.5 w-3.5 mr-1" />
      )}
      {copied ? "Copied" : `Copy all emails${disabled ? "" : ` (${emails.length})`}`}
    </Button>
  );
}
